import React from 'react';
import { CartSummaryProps, cn, formatNumber, InputType, TTagSeverity } from 'types';
import { Tag } from '../tags';
import { Accordion } from '../accordion';
import { VehicleConfigKeys } from 'next-common';
import { VatCode } from 'sales-types-common';
import { InputField } from '../input-field';
import { SmartLink } from '../link';

export function CartSummary(props: Readonly<CartSummaryProps>) {
  const [updatedTaxAndWreckage, setUpdatedTaxAndWreckage] = React.useState<number>();
  const [doneReset, setDoneReset] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (props.oneOffTaxAndWreckageDetails?.taxAndWreckageValue >= 0) {
      setUpdatedTaxAndWreckage(props.oneOffTaxAndWreckageDetails?.taxAndWreckageValue);
    }
  }, [props.oneOffTaxAndWreckageDetails?.taxAndWreckageValue]);

  const handleResetInput = () => {
    setDoneReset(true);
    setUpdatedTaxAndWreckage(props.oneOffTaxAndWreckageDetails?.resetTaxAndWreckageValue);
    props.oneOffTaxAndWreckageDetails.inputValue(props.oneOffTaxAndWreckageDetails?.resetTaxAndWreckageValue, true);
  };

  const handleBruksKodeTagClick = () => {
    if (typeof props.bruksKodeTag?.onTagClick === 'function') {
      props.bruksKodeTag?.onTagClick();
    }
  };

  return (
    <div className="w-full h-full">
      <div className="relative h-full p-6">
        {props.bruksKodeTag?.isTagVisible && (
          <div
            className={cn(
              'absolute right-6 top-7',
              typeof props.bruksKodeTag?.onTagClick === 'function' ? 'cursor-pointer' : 'cursor-not-allowed'
            )}
          >
            <Tag
              severity={TTagSeverity.NEUTRAL}
              className={cn(
                '',
                typeof props.bruksKodeTag?.onTagClick === 'function'
                  ? 'cursor-pointer'
                  : 'text-black cursor-not-allowed'
              )}
              onClick={
                typeof props.bruksKodeTag?.onTagClick === 'function' ? () => handleBruksKodeTagClick() : undefined
              }
            >
              <span className="text-sm/[170%]">{props.bruksKodeTag?.label}</span>
            </Tag>
          </div>
        )}
        <div className="w-full bottom-0 left-0">
          <Accordion
            customLabel={
              <div className="text-sm font-extrabold not-italic leading-[170%]">Gjeldende konfigurasjon</div>
            }
            defaultOpen
            className="tw-grey-mode flex flex-col w-full"
            triggerClassName="w-fit rounded-b-none"
            contentClassName="p-0 pb-2"
          >
            {props.data?.map((ele, index) => {
              const pricePrefix = ele?.price > 0 && index > 0 ? '+ ' : '';
              const displayValue = ele?.isRecommended
                ? 'Inkludert'
                : `${pricePrefix}${formatNumber(ele?.price ?? 0)} kr`;
              return ele?.key === VehicleConfigKeys.MARGIN && ele?.price === 0 ? null : (
                <CartRow
                  key={ele?.id}
                  label={ele?.name}
                  vatCode={ele?.vatCode}
                  value={
                    <div className="flex items-center gap-8">
                      {props.rammeavtale && ele?.percentage && (
                        <Tag className="rounded-full" severity={TTagSeverity.SUCCESS}>
                          <span className="text-sm/[170%] font-semibold">{`-${ele.percentage}`}</span>
                        </Tag>
                      )}
                      {displayValue}
                    </div>
                  }
                />
              );
            })}
            {props.equipmentDetails?.length > 0 && (
              <div className="border border-x-0 border-t-0 leading-4 border-b-neutralGrey w-full flex flex-col items-start gap-2 self-stretch">
                <Accordion
                  customLabel={
                    <span className="text-base font-extrabold leading-[175%] text-concrete">Ekstrautstyr</span>
                  }
                  className="tw-small w-full h-full py-2 border-0 tw-grey-mode bg-white enabled:hover:bg-white"
                  triggerClassName="border-0 bg-white enabled:hover:bg-white"
                  contentClassName="border-0 aria-expanded:pt-2"
                >
                  <div className="flex flex-col gap-2">
                    {props.equipmentDetails?.map((equipment, key) => (
                      <CartRow
                        key={equipment?.key}
                        className="border-none p-0 pl-3"
                        valueClass="text-concrete"
                        label={
                          <div className="flex items-baseline">
                            <div className="bg-concrete rounded-full w-1.5 h-1.5 shrink-0" />
                            <span className="text-base/[175%] text-concrete font-semibold pl-2">
                              {equipment?.title} {equipment?.vatCode === VatCode.DEDUCTABLE && ' *'}
                            </span>
                          </div>
                        }
                        labelClass="flex"
                        value={equipment?.value}
                        vatCode={equipment.vatCode}
                      />
                    ))}
                  </div>
                </Accordion>
              </div>
            )}
            {/* done */}
            <CartRow
              label="Sum eks. mva"
              labelClass="font-extrabold text-concrete"
              valueClass="font-extrabold text-concrete"
              className={`${props.isElectricCar ? 'not-first:border-b-0 not-first:pb-0' : ''}`}
              value={`${formatNumber(props.prices?.salesTotalExcludingVatAndTax ?? 0)} kr`}
            />
            {props.isElectricCar && (
              <CartRow
                className="border-t-0 pt-2"
                label="Kjøpsbeløp over 300 000 kr som er mva. pliktig"
                value={`${formatNumber(props.prices?.electrifiedCarVatCalculationBasis ?? 0)} kr`}
                labelClass="text-sm/[170%] font-semibold"
                valueClass="text-sm/[170%] font-semibold"
              />
            )}

            {props.oneOffTaxChildren ? (
              props.oneOffTaxChildren
            ) : (
              <CartRow className="flex-col gap-4">
                <div className="justify-between flex items-start gap-8 self-stretch p-0">
                  <div className="flex flex-col flex-1">
                    <label
                      htmlFor="updatedTaxAndWreckage"
                      className="flex-1 text-base/[175%] text-concrete font-semibold"
                    >
                      Estimert engangsavgift/vrakpant
                    </label>
                    {props.oneOffTaxAndWreckageDetails?.isEditable && (
                      <label className="w-fit flex gap-1 items-center text-base/[170%]">
                        Overstyrt,{' '}
                        <SmartLink
                          customClasses={'text-[var(--color-turquoise)] border-[var(--color-turquoise)]'}
                          className="h-fit text-base/[170%]"
                          onLinkClick={handleResetInput}
                        >
                          <span className="text-black font-semibold text-base">tilbakestill</span>
                        </SmartLink>
                      </label>
                    )}
                  </div>
                  {props.oneOffTaxAndWreckageDetails?.isEditable ? (
                    <div className="flex items-center gap-2 inputSection">
                      <span className="text-black text-right text-base font-semibold leading-[1.75] inputTitle">+</span>
                      <InputField
                        type={InputType.INTEGER}
                        value={formatNumber(updatedTaxAndWreckage) ?? ''}
                        className="w-22 py-4 px-3"
                        onChange={(value: number) => {
                          setDoneReset(false);
                          setUpdatedTaxAndWreckage(
                            value >= 0 ? value : props.oneOffTaxAndWreckageDetails?.taxAndWreckageValue
                          );
                        }}
                        onBlur={() => {
                          props.oneOffTaxAndWreckageDetails.inputValue(updatedTaxAndWreckage, doneReset);
                          setUpdatedTaxAndWreckage(updatedTaxAndWreckage);
                        }}
                      />
                      <span className="text-black text-right text-base font-semibold leading-[1.75]">kr</span>
                    </div>
                  ) : (
                    <p className="text-black font-semibold text-base not-italic leading-[1.6] tracking-[0.18px] m-0">{`+ ${formatNumber(
                      props.oneOffTaxAndWreckageDetails?.taxAndWreckageValue ?? 0
                    )} kr`}</p>
                  )}
                </div>
              </CartRow>
            )}

            <CartRow
              label="Veiledende pris inkl. mva og avgifter"
              className="border-none"
              value={`${formatNumber(props.prices?.calculatedSalesTotalBeforeDiscount ?? 0)} kr`}
            />
            <CartRow
              label="Rabatt på bil"
              className="border-none py-0"
              value={`- ${formatNumber(props.prices?.calculatedDiscountOnVehicle || 0)} kr`}
            />
            {props.rammeavtale && !!props.prices?.calculatedDiscountOnAccessories && (
              <CartRow
                label="Rabatt på tilbehør"
                className="border-none py-0"
                value={`- ${formatNumber(props.prices?.calculatedDiscountOnAccessories || 0)} kr`}
              />
            )}
            {props.rammeavtale && !!props.prices?.calculatedDiscountOnDeliveryCost && (
              <CartRow
                label="Rabatt på leveringsomkostninger"
                className="border-none py-0"
                value={`- ${formatNumber(props.prices?.calculatedDiscountOnDeliveryCost || 0)} kr`}
              />
            )}
            <CartRow
              label="Totalpris inkl. mva. og avgifter"
              labelClass="font-extrabold text-black"
              valueClass="font-extrabold text-black"
              value={`${formatNumber(props.prices?.salesTotalIncludingVatAndTax ?? 0)} kr`}
            />
            <CartRow
              label="mva. på kjøpsbeløp"
              labelClass="text-sm/[170%]"
              valueClass="text-sm/[170%] text-concrete"
              className={cn('border-none', props.isElectricCar ? 'pb-1' : '')}
              value={
                props.isElectricCar
                  ? `${formatNumber(props.prices?.calculatedVatOnDeductableItems ?? 0)} kr`
                  : `${formatNumber(props.prices?.calculatedVatOnSalesTotal ?? 0)} kr`
              }
            />
            {props.isElectricCar && (
              <CartRow
                className="pt-0 border-none"
                label="mva. på tilbehør"
                labelClass="text-sm/[170%]"
                valueClass="text-sm/[170%]"
                value={`${formatNumber(props.prices?.calculatedVatOnNonDeductableItems ?? 0)} kr`}
              />
            )}
          </Accordion>
        </div>
      </div>
    </div>
  );
}

export const CartRow = ({
  label,
  value,
  className = '',
  labelClass = '',
  valueClass = '',
  vatCode = '',
  children,
}: {
  label?: string | React.ReactNode;
  value?: string | React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  labelClass?: string;
  valueClass?: string;
  vatCode?: string;
}) => (
  <div className={cn('flex justify-between gap-8 p-4 border-b border-neutralGrey first:border-t', className)}>
    {children ?? (
      <>
        <span className={cn('flex-1 text-base/[175%] text-concrete font-semibold', labelClass)}>
          {label}
          {vatCode === VatCode.DEDUCTABLE && '*'}
        </span>
        <span className={cn('text-base/[175%] text-black font-semibold text-right', valueClass)}>{value}</span>
      </>
    )}
  </div>
);
